import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { calculateDataGridLayout, DataGridLayout } from './data-grid-layout';

interface Props {
  children: ReactNode;
  columnCount: number;
  onScrollPositionChange?: (scrollLeft: number) => void;
}

/** Read channel used by grid components to consume the calculated dimensions and overflow state. */
export const DataGridLayoutContext = createContext<DataGridLayout | null>(null);

/**
 * Private registration channel used by the sticky header to provide the DOM element that must be measured.
 * It is separate from the public layout context because report data can mount the header after this provider commits;
 * a callback ref makes that attachment observable without exposing measurement setup to layout consumers.
 */
const DataGridViewportRefContext = createContext<React.RefCallback<HTMLDivElement> | undefined>(undefined);

/**
 * Measures the rendered data-grid viewport and shares one fit-or-scroll layout with all grids on the page.
 *
 * The registered viewport must be the sticky header's `.tableInner` element so its client width already accounts
 * for page padding and the row-header area.
 */
export const DataGridLayoutProvider = ({ children, columnCount, onScrollPositionChange }: Props) => {
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const previousIsScrollableRef = useRef(columnCount > 0);
  const scrollLeftBeforeFitRef = useRef(0);
  const viewportRef = useCallback((element: HTMLDivElement | null) => setViewport(element), []);

  const layout = useMemo(() => calculateDataGridLayout(columnCount, viewportWidth), [columnCount, viewportWidth]);

  useLayoutEffect(() => {
    if (!viewport) {
      return;
    }

    /** Updates layout from the usable viewport width, which already excludes page padding and the row header. */
    const measureViewport = () => setViewportWidth(viewport.clientWidth);

    measureViewport();

    if (typeof ResizeObserver !== 'undefined') {
      const observerCallback: ResizeObserverCallback = (_entries, _observer) => {
        measureViewport();
      };
      const observer = new ResizeObserver(observerCallback);
      observer.observe(viewport);

      return () => observer.disconnect();
    }

    window.addEventListener('resize', measureViewport);
    return () => window.removeEventListener('resize', measureViewport);
  }, [viewport]);

  useEffect(() => {
    if (viewportWidth <= 0 || previousIsScrollableRef.current === layout.isScrollable) {
      return;
    }

    if (layout.fitsInViewport) {
      scrollLeftBeforeFitRef.current = viewport?.scrollLeft || 0;
      onScrollPositionChange?.(0);
    } else {
      onScrollPositionChange?.(scrollLeftBeforeFitRef.current);
    }

    previousIsScrollableRef.current = layout.isScrollable;
  }, [layout.fitsInViewport, layout.isScrollable, onScrollPositionChange, viewport, viewportWidth]);

  return (
    <DataGridViewportRefContext.Provider value={viewportRef}>
      <DataGridLayoutContext.Provider value={layout}>{children}</DataGridLayoutContext.Provider>
    </DataGridViewportRefContext.Provider>
  );
};

/** Returns the measured page-level grid layout, or `null` when a grid is not inside a layout provider. */
export const useDataGridLayout = (): DataGridLayout | null => useContext(DataGridLayoutContext);

/** Registers the rendered element used as the page-level grid viewport. */
export const useDataGridViewportRef = (): React.RefCallback<HTMLDivElement> | undefined =>
  useContext(DataGridViewportRefContext);
